import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Heart, ExternalLink, Loader2 } from "lucide-react";

export default function Sponsors() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/sponsors/wall")
      .then((r) => setData(r.data))
      .catch(() => setData({ live: [], past: [], total: 0 }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="min-h-[40vh] flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  }

  const live = data?.live || [];
  const past = data?.past || [];

  return (
    <div className="bg-white" data-testid="sponsors-page">
      <section className="border-b-2 border-zinc-900 bg-gradient-to-br from-amber-50 to-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-16 text-center">
          <div className="inline-flex items-center gap-2 border border-zinc-900 bg-white px-3 py-1.5 text-[11px] uppercase tracking-widest font-bold mb-6">
            <Heart size={12} className="fill-red-600 text-red-600" /> Donors Wall
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
            Thank you to our <span className="text-red-600">sponsors</span>.
          </h1>
          <p className="mt-6 text-lg text-zinc-700 max-w-2xl mx-auto leading-relaxed">
            Every name on this wall is a business or organization that donated $100 to keep Paws on Record
            free, searchable, and independent. Their support directly helps shelters, fosters, and
            adoption-minded families vet applicants before placing animals.
          </p>
          <div className="mt-8 flex items-center justify-center gap-6 flex-wrap text-sm">
            <Stat num={live.length} label="Live now" testId="sponsors-stat-live" pulse />
            <Stat num={past.length} label="Past supporters" testId="sponsors-stat-past" />
            <Stat num={data?.total || 0} label="Total contributions" testId="sponsors-stat-total" />
          </div>
        </div>
      </section>

      {live.length === 0 && past.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          {live.length > 0 && (
            <Section
              title="Live sponsors"
              subtitle="Currently displaying in the registry sidebar"
              items={live}
              live
            />
          )}
          {past.length > 0 && (
            <Section
              title="Past supporters"
              subtitle="Previously sponsored Paws on Record — thank you!"
              items={past}
            />
          )}
        </>
      )}

      {/* CTA */}
      <section className="border-t-2 border-zinc-900 bg-zinc-900 text-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 py-16 text-center">
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight">
            Want your name on this wall?
          </h2>
          <p className="mt-4 text-zinc-300">$100 sponsor donation. No subscription. Cancel anytime.</p>
          <Link
            to="/advertise"
            className="mt-6 inline-flex items-center gap-2 bg-white text-zinc-900 px-8 py-4 font-bold uppercase tracking-widest text-sm hover:bg-amber-300 transition-colors"
            data-testid="sponsors-cta-advertise"
          >
            Become a sponsor →
          </Link>
        </div>
      </section>
    </div>
  );
}

function Section({ title, subtitle, items, live }) {
  return (
    <section className="border-b border-zinc-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14">
        <div className="flex items-baseline gap-3 mb-6">
          <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight" data-testid={`sponsors-section-${live ? "live" : "past"}`}>
            {title}
          </h2>
          {live && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-green-700">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> Live
            </span>
          )}
        </div>
        <p className="text-sm text-zinc-600 mb-8">{subtitle}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {items.map((s) => (
            <a
              key={s.id}
              href={s.ad_link_url}
              target="_blank"
              rel="noreferrer sponsored nofollow"
              className="group block border border-zinc-300 bg-white hover:border-zinc-900 transition-colors"
              data-testid={`sponsor-card-${s.id}`}
            >
              <div className="aspect-[4/3] bg-zinc-100 overflow-hidden">
                <img
                  src={s.ad_image_url}
                  alt={s.ad_title}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              </div>
              <div className="p-4">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                  {s.advertiser_name}
                </div>
                <div className="font-display font-bold text-sm leading-snug mt-1 line-clamp-2">
                  {s.ad_title}
                </div>
                <div className="mt-3 flex items-center gap-1 text-[11px] font-mono text-zinc-500 group-hover:text-red-600">
                  Visit <ExternalLink size={11} />
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function EmptyState() {
  return (
    <section className="max-w-3xl mx-auto px-6 lg:px-8 py-20 text-center" data-testid="sponsors-empty">
      <h2 className="font-display font-black text-2xl">Be the first sponsor</h2>
      <p className="mt-3 text-zinc-600">
        No one has donated yet — your business could be the first name permanently on this wall.
      </p>
      <Link
        to="/advertise"
        className="mt-6 inline-flex items-center gap-2 bg-zinc-900 text-white px-6 py-3 font-bold uppercase tracking-widest text-sm hover:bg-red-600 transition-colors"
      >
        Become the first sponsor →
      </Link>
    </section>
  );
}

function Stat({ num, label, testId, pulse }) {
  return (
    <div data-testid={testId} className="flex items-baseline gap-2">
      {pulse && <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />}
      <span className="font-display font-black text-2xl tracking-tighter">{num}</span>
      <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">{label}</span>
    </div>
  );
}
